import {
  Component,
  ComponentRef,
  DestroyRef,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { Tenant, TenantNames } from '@mynexus/mynexus-ui-lib';
import { PortalProducts } from '@shared/enumeration';
import { LineOfBusiness, TenantService } from 'src/app/core';
import { ElementRegistryService } from '@shared/services/element-registry.service';
import { PlatformService } from '@core/service/platform.service';
import { QueueDMEPOSService } from '@products/dmepos/components/queue/queue.service';
import { ReferenceByEntity } from '@core/model/reference-by-entity';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';

type MemberRouterEvent = {
  type: 'member';
  payload: {
    tenantName: string;
    memberSubscriberNumber: string;
  };
};

type AuthorizationRouterEvent = {
  type: 'authorization';
  payload: {
    tenantName: string;
    authorizationNumber: string;
    authorizationCaseId?: string;
  };
};

type QueueRouterEvents = MemberRouterEvent | AuthorizationRouterEvent;

interface QueueWebComponent {
  reset(): void;
  reloadTable(): void;
  filterStatusReasons: ReferenceByEntity[];
}

@Component({
  selector: 'dmepos-queue',
  templateUrl: './queue.component.html',
  styleUrls: ['./queue.component.scss'],
  providers: [QueueDMEPOSService],
  standalone: false,
})
export class QueueComponent implements OnInit, OnDestroy {
  @ViewChild('queue') queue: ElementRef<QueueWebComponent>;

  stringifiedLineOfBusiness: string;
  stringifiedTenants: string;

  @Input() set lineOfBusiness(value: LineOfBusiness) {
    this.platformService
      .getLineOfBusinesses()
      .pipe(take(1))
      .subscribe(
        (res) => (this.stringifiedLineOfBusiness = JSON.stringify(res[value]))
      );
  }

  statusReasons: ReferenceByEntity[] = [];
  selectedStatusReasons: ReferenceByEntity[] = [];
  tenants: Record<TenantNames, Tenant>;
  isLoading = true;

  private listenerWasAdded = false;

  constructor(
    private readonly destroyRef: DestroyRef,
    private readonly queueService: QueueDMEPOSService,
    private readonly router: Router,
    private readonly tenantService: TenantService,
    private elementRegistry: ElementRegistryService,
    private platformService: PlatformService
  ) {}

  ngOnInit() {
    if (this.elementRegistry.scriptsHaveBeenLoaded.has('mnc-dmepos')) {
      this.isLoading = false;
    } else {
      // listen event when all mnc-dmepos components are loaded
      document.addEventListener(
        'mnc-dmepos-loaded',
        () => (this.isLoading = false)
      );
      this.listenerWasAdded = true;

      // use it to load mnc-dmepos component instead of lazyComponent in HTML template
      this.elementRegistry.prepare('mnc-dmepos');
    }

    this.tenantService
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((tenants) => {
        this.tenants = tenants;
        this.stringifiedTenants = JSON.stringify(tenants);
      });

    this.queueService
      .getStatusReasons()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.statusReasons = res[0].children.sort(
            (a, b) => a.sortOrder - b.sortOrder
          );
          const statusReasonsFromStorage =
            JSON.parse(
              sessionStorage.getItem(`dmepos-ma-queue-status-reasons`)
            ) || [];
          const selectedStatusReasons = this.statusReasons.filter((reason) =>
            statusReasonsFromStorage.find(
              (selectedReason) =>
                selectedReason.referenceByEntityId ===
                reason.referenceByEntityId
            )
          );
          this.setFilterStatusReasons(selectedStatusReasons);
        },
      });
  }

  ngOnDestroy(): void {
    if (this.listenerWasAdded) {
      document.removeAllListeners('mnc-dmepos-loaded');
    }
  }

  clear() {
    this.selectedStatusReasons = [];
    this.queue.nativeElement.reset();
    this.queue.nativeElement.filterStatusReasons = [];
    sessionStorage.removeItem(`dmepos-ma-queue-status-reasons`);
  }

  reload() {
    this.queue.nativeElement.reloadTable();
  }

  handleRouterEvents({ detail }: CustomEvent<QueueRouterEvents>) {
    const { type, payload } = detail;
    const tenantId = this.tenants[payload.tenantName].tenantId;

    switch (type) {
      case 'member':
        this.router.navigate([
          tenantId,
          'member',
          payload.memberSubscriberNumber,
        ]);
        break;
      case 'authorization':
        this.router.navigate(
          [
            tenantId,
            PortalProducts.DMEPOS,
            'auth-dashboard',
            payload.authorizationNumber,
          ],
          {
            queryParams: payload.authorizationCaseId
              ? { caseId: payload?.authorizationCaseId }
              : {},
          }
        );
    }
  }

  setFilterStatusReasons(statusReasons: ReferenceByEntity[]) {
    this.selectedStatusReasons = statusReasons;
    this.queue.nativeElement.filterStatusReasons = statusReasons;

    const allSelected = this.statusReasons.every((reason) =>
      statusReasons.includes(reason)
    );
    const selectedStatusReasons = allSelected ? [] : statusReasons;

    sessionStorage.setItem(
      `dmepos-ma-queue-status-reasons`,
      JSON.stringify(selectedStatusReasons)
    );
  }
}
